import React from 'react';
import {
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  useTheme,
} from '@mui/material';
import { THEME_COLORS, Z_INDEX } from '../../constants/theme';
import { getDrawerSelectMenuProps } from '../../../utils/drawerOverlayProps';

/**
 * Reusable Select Component
 * 
 * Provides consistent theming and styling for dropdown selects.
 * Supports custom MenuProps for z-index control in drawers/modals.
 * 
 * @component
 * @example
 * <FormSelect
 *   label="Room Type"
 *   value={roomType}
 *   onChange={(e) => setRoomType(e.target.value)}
 * >
 *   <MenuItem value="standard">Standard</MenuItem>
 *   <MenuItem value="deluxe">Deluxe</MenuItem>
 * </FormSelect>
 */
const FormSelect = ({ 
  label, 
  value, 
  onChange, 
  children, 
  fullWidth = true,
  size = 'small',
  disabled = false,
  sx = {},
  inDrawer = false, // If true, uses higher z-index for menu
  ...props 
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Always portal above drawers (mobile MuiDrawer = 1700). disablePortal clips
  // inside overflow:auto drawer bodies and sits under the drawer modal.
  const menuProps = inDrawer
    ? getDrawerSelectMenuProps(isDark)
    : {
        disablePortal: false,
        sx: { zIndex: Z_INDEX.MENU },
        PaperProps: {
          sx: {
            zIndex: Z_INDEX.MENU,
            backgroundImage: 'none',
          },
        },
      };

  return (
    <FormControl 
      fullWidth={fullWidth} 
      size={size}
      disabled={disabled}
      sx={sx}
    >
      {label && (
        <InputLabel 
          sx={{ 
            color: isDark ? THEME_COLORS.TEXT_SECONDARY_DARK : THEME_COLORS.TEXT_SECONDARY_LIGHT,
            '&.Mui-focused': {
              color: THEME_COLORS.PRIMARY,
            },
          }}
        >
          {label}
        </InputLabel>
      )}
      <MuiSelect
        value={value}
        label={label}
        onChange={onChange}
        MenuProps={menuProps}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: THEME_COLORS.PRIMARY,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: THEME_COLORS.PRIMARY,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: THEME_COLORS.PRIMARY,
          },
          color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
          '& .MuiSelect-select': {
            color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
          },
        }}
        {...props}
      >
        {children}
      </MuiSelect>
    </FormControl>
  );
};

export default FormSelect;

